import { T, useGT } from 'gt-react';
import {
  EXTENSION_TARGET_MODELS,
  isExtensionTargetModel,
  type ExtensionTargetModel,
} from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useExtendedTargetModels } from 'src/api/rpc/hooks/core/useSchemaExtensions';
import { useTargetModelLabel } from 'src/components/common/schema-extensions/extensionTargetLabels';

/**
 * Picks the entity a new extension adds fields to. An entity takes one
 * extension, so entities that already have one are listed but disabled. If
 * that can't be checked, nothing is offered: an unknown entity may be taken.
 */
export function ExtensionEntitySelect({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: ExtensionTargetModel | null;
  onChange: (value: ExtensionTargetModel | null) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  const targetModelLabel = useTargetModelLabel();
  const extendedTargets = useExtendedTargetModels(true);

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm">
        <T context="label entity an extension schema extends">Entity</T>
      </Label>
      <Select
        value={value ?? ''}
        onValueChange={(v) => onChange(isExtensionTargetModel(v) ? v : null)}
        disabled={
          disabled || extendedTargets.isLoading || extendedTargets.isError
        }
      >
        <SelectTrigger id={id}>
          <SelectValue
            placeholder={gt('Choose an entity', {
              context: 'placeholder entity select quick schema',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          {EXTENSION_TARGET_MODELS.map((model) => (
            <SelectItem
              key={model}
              value={model}
              disabled={extendedTargets.extended.has(model)}
            >
              {extendedTargets.extended.has(model)
                ? gt('{entity} (already extended)', {
                    context: 'entity option that already has an extension',
                    entity: targetModelLabel(model),
                  })
                : targetModelLabel(model)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {extendedTargets.isError && (
        <div role="alert" className="flex items-center gap-2">
          <p className="text-xs text-destructive">
            <T context="error checking which entities already have an extension">
              Couldn’t check which entities already have an extension.
            </T>
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={extendedTargets.refetch}
          >
            <T context="button that retries checking entity extensions">
              Retry
            </T>
          </Button>
        </div>
      )}
    </div>
  );
}
